import React, { useMemo, useState } from 'react';
import { FlatList, Modal, Platform, Pressable, StyleSheet, TextInput as RNTextInput, View } from 'react-native';

import Text from '../Text';
import { Entypo, FontAwesome, Ionicons } from '../Icons';
import { useStyles } from '~/hooks';
import { useTheme } from '~/hooks/useTheme';
import i18n from '~/i18n';
import { FONT_SIZE_M, FONT_SIZE_S, FONT_SIZE_XS } from '~/constants/typography';
import { isDesktop, scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

export type SelectOption = {
    value: string;
    // Display label (already translated / human-readable).
    label: string;
    sublabel?: string;
};

type Props = {
    visible: boolean;
    onClose: () => void;
    // Translated via the core Text component (i18n key or plain text).
    title: string;
    options: SelectOption[];
    selectedValue?: string | null;
    onSelect: (value: string) => void;
    // i18n key for the search placeholder.
    searchPlaceholder?: string;
    // Short lists (currencies, states) read better without a search box above three rows.
    searchable?: boolean;
};

/**
 * Searchable picker for long option lists (instruments, indicators…): full-screen sheet on
 * phones, centered dialog on desktop. Filters on label, sublabel and value; selecting an
 * option closes the picker.
 */
export default function SearchableSelect({
    visible,
    onClose,
    title,
    options,
    selectedValue,
    onSelect,
    searchPlaceholder = 'search',
    searchable = true,
}: Props) {
    const styles = useStyles(stylesGenerator);
    const theme = useTheme();
    const [query, setQuery] = useState('');
    const [searchFocused, setSearchFocused] = useState(false);
    const desktop = isDesktop();

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) {
            return options;
        }
        return options.filter(
            (o) =>
                o.label.toLowerCase().includes(q) ||
                o.value.toLowerCase().includes(q) ||
                (o.sublabel ?? '').toLowerCase().includes(q)
        );
    }, [options, query]);

    const close = () => {
        setQuery('');
        onClose();
    };

    return (
        <Modal visible={visible} transparent animationType={desktop ? 'fade' : 'slide'} onRequestClose={close}>
            <View style={[styles.backdrop, desktop ? styles.backdropDesktop : null]}>
                {desktop ? <Pressable style={StyleSheet.absoluteFill} onPress={close} /> : null}
                <View style={[styles.panel, desktop ? styles.panelDesktop : null]}>
                    <View style={styles.headerRow}>
                        <Text fontType="LATO_BOLD" style={styles.title}>
                            {title}
                        </Text>
                        <Pressable hitSlop={8} onPress={close} accessibilityLabel="Close">
                            <Ionicons name="close" size={scaleSize(22)} style={styles.closeIcon} />
                        </Pressable>
                    </View>
                    {searchable ? (
                        <View style={[styles.searchRow, searchFocused ? styles.searchRowFocused : null]}>
                            <FontAwesome name="search" size={scaleSize(14)} style={styles.searchIcon} />
                            <RNTextInput
                                style={styles.searchInput}
                                value={query}
                                onChangeText={setQuery}
                                placeholder={i18n.t(searchPlaceholder)}
                                placeholderTextColor={theme.textSecondary}
                                autoCorrect={false}
                                autoCapitalize="none"
                                onFocus={() => setSearchFocused(true)}
                                onBlur={() => setSearchFocused(false)}
                            />
                        </View>
                    ) : null}
                    <FlatList
                        data={filtered}
                        keyExtractor={(item) => item.value}
                        keyboardShouldPersistTaps="handled"
                        initialNumToRender={20}
                        renderItem={({ item }) => {
                            const selected = item.value === selectedValue;
                            return (
                                <Pressable
                                    onPress={() => {
                                        onSelect(item.value);
                                        close();
                                    }}
                                    style={({ pressed }) => [styles.optionRow, pressed && styles.optionRowPressed]}
                                >
                                    <View style={styles.optionTextWrap}>
                                        <Text
                                            noTranslate
                                            fontType={selected ? 'LATO_BOLD' : 'LATO_REGULAR'}
                                            style={styles.optionLabel}
                                        >
                                            {item.label}
                                        </Text>
                                        {item.sublabel ? (
                                            <Text noTranslate style={styles.optionSublabel}>
                                                {item.sublabel}
                                            </Text>
                                        ) : null}
                                    </View>
                                    {selected ? (
                                        <Entypo name="check" size={scaleSize(16)} style={styles.checkIcon} />
                                    ) : null}
                                </Pressable>
                            );
                        }}
                        ListEmptyComponent={<Text style={styles.emptyText}>no-results-found</Text>}
                    />
                </View>
            </View>
        </Modal>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        backdrop: {
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            justifyContent: 'flex-end',
        },
        backdropDesktop: {
            justifyContent: 'center',
            alignItems: 'center',
        },
        panel: {
            backgroundColor: theme.background,
            borderTopLeftRadius: scaleSize(16),
            borderTopRightRadius: scaleSize(16),
            maxHeight: '92%',
            paddingHorizontal: scaleSize(16),
            paddingBottom: scaleSize(12),
        },
        panelDesktop: {
            width: 480,
            maxWidth: '90%',
            maxHeight: '85%',
            borderRadius: scaleSize(12),
        },
        headerRow: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: scaleSize(14),
            paddingBottom: scaleSize(8),
        },
        title: {
            fontSize: FONT_SIZE_M,
            color: theme.text,
        },
        closeIcon: {
            color: theme.textSecondary,
        },
        searchRow: {
            flexDirection: 'row',
            alignItems: 'center',
            borderWidth: 1,
            borderColor: theme.equityValue,
            borderRadius: scaleSize(8),
            backgroundColor: theme.backgroundSecondary,
            paddingHorizontal: scaleSize(10),
            marginBottom: scaleSize(8),
        },
        searchRowFocused: {
            borderColor: theme.button,
        },
        searchIcon: {
            color: theme.textSecondary,
            marginRight: scaleSize(6),
        },
        searchInput: {
            flex: 1,
            color: theme.text,
            fontSize: FONT_SIZE_S,
            paddingVertical: scaleSize(9),
            // The row's own border carries the focus state; the UA ring would sit inside it.
            ...Platform.select({ web: { outlineStyle: 'none' } as object, default: {} }),
        },
        optionRow: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingVertical: scaleSize(11),
            borderBottomWidth: StyleSheet.hairlineWidth,
            borderBottomColor: theme.equityValue,
        },
        optionRowPressed: {
            opacity: 0.6,
        },
        optionTextWrap: {
            flex: 1,
            marginRight: scaleSize(8),
        },
        optionLabel: {
            fontSize: FONT_SIZE_S,
            color: theme.text,
        },
        optionSublabel: {
            fontSize: FONT_SIZE_XS,
            color: theme.textSecondary,
            marginTop: scaleSize(1),
        },
        checkIcon: {
            color: theme.button,
        },
        emptyText: {
            fontSize: FONT_SIZE_S,
            color: theme.textSecondary,
            textAlign: 'center',
            paddingVertical: scaleSize(24),
        },
    });
